/**
 * EM-J: DMARC aggregate (rua) report ingestion.
 *
 * WHY: EM-E audit (#795) flagged DMARC as STUB — the `rua=` tag was published
 * in DNS but inbound aggregate reports from mailbox providers (Gmail, Yahoo,
 * Microsoft, etc.) had nowhere to land. Without persistence we cannot detect
 * spoofing, alignment failures, or unauthorized senders against tenant domains.
 *
 * One row per aggregate report (RFC 7489 §7.2.1 `feedback`). Records
 * (per source-IP rows inside the report) live as JSONB to keep ingest atomic;
 * read paths can fan out into a child table later if needed for OLAP.
 *
 * Idempotency: `(tenant_id, org_name, report_id)` is the natural key —
 * mailbox providers re-deliver on transient errors and we MUST NOT double-count.
 */
import {
  pgTable,
  text,
  timestamp,
  uuid,
  integer,
  jsonb,
  index,
  uniqueIndex,
} from "drizzle-orm/pg-core";

const tz = { withTimezone: true } as const;

/** Per-row record summary parsed out of `<record>` elements in the XML body. */
export interface DmarcReportRecord {
  /** Reporting source IP (v4 or v6). */
  sourceIp: string;
  /** Count of messages from this IP in the report window. */
  count: number;
  /** Disposition policy applied: none | quarantine | reject. */
  disposition: "none" | "quarantine" | "reject";
  /** DKIM alignment result: pass | fail. */
  dkim: "pass" | "fail";
  /** SPF alignment result: pass | fail. */
  spf: "pass" | "fail";
  /** Header-From domain claimed by the message. */
  headerFrom: string;
  /** Reasons the disposition was overridden (forwarded, mailing list, etc.). */
  overrideReasons?: string[];
}

export const DMARC_DISPOSITIONS = ["none", "quarantine", "reject"] as const;
export type DmarcDisposition = (typeof DMARC_DISPOSITIONS)[number];

export const dmarcReports = pgTable(
  "dmarc_reports",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    /** Domain the report covers (the policy domain, e.g. mail.acme.com). */
    policyDomain: text("policy_domain").notNull(),
    /** `org_name` from `<report_metadata>` — e.g. "google.com", "Yahoo". */
    orgName: text("org_name").notNull(),
    /** `email` of the reporting org. */
    orgEmail: text("org_email"),
    /** `report_id` from `<report_metadata>` — provider-assigned identifier. */
    reportId: text("report_id").notNull(),
    /** `date_range/begin` (unix seconds in XML, stored as timestamptz). */
    dateRangeBegin: timestamp("date_range_begin", tz).notNull(),
    /** `date_range/end`. */
    dateRangeEnd: timestamp("date_range_end", tz).notNull(),
    /** Total messages summed across all `<record>` rows. */
    totalMessages: integer("total_messages").notNull().default(0),
    /** Messages that failed DKIM alignment. */
    dkimFailCount: integer("dkim_fail_count").notNull().default(0),
    /** Messages that failed SPF alignment. */
    spfFailCount: integer("spf_fail_count").notNull().default(0),
    /** Full parsed record list — small enough to keep inline (typical < 50 KB). */
    records: jsonb("records").$type<DmarcReportRecord[]>().notNull(),
    ingestedAt: timestamp("ingested_at", tz).notNull().defaultNow(),
  },
  (table) => [
    // Idempotency: providers re-deliver — same (tenant, org, report_id) MUST collapse.
    uniqueIndex("idx_dmarc_reports_tenant_org_report").on(
      table.tenantId,
      table.orgName,
      table.reportId,
    ),
    index("idx_dmarc_reports_tenant_window").on(
      table.tenantId,
      table.dateRangeBegin,
    ),
    index("idx_dmarc_reports_policy_domain").on(
      table.tenantId,
      table.policyDomain,
    ),
  ],
);
